import React, { useState } from 'react';
import { useNavigate, useLocation, Navigate, Link } from 'react-router-dom';
import { useConsumerAuth } from 'context/ConsumerAuthProvider';
import { Building2, User, KeyRound, LogIn, AlertCircle, Loader2, Eye, EyeOff } from 'lucide-react';

const ConsumerLogin = () => {
  const { login, token } = useConsumerAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (token) {
    const dest = (location.state as { from?: { pathname?: string } })?.from?.pathname || '/consumer/dashboard';
    return <Navigate to={dest} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    const result = await login(username.trim(), password);
    setSubmitting(false);
    if (result.ok) {
      const dest = (location.state as { from?: { pathname?: string } })?.from?.pathname || '/consumer/dashboard';
      navigate(dest, { replace: true });
    } else {
      setError(result.message || 'Login failed.');
    }
  };

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-2 bg-app">
      <div className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-gradient-to-br from-slate-900 via-indigo-950 to-brand-dark p-12 text-white">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-indigo-400/10 blur-3xl" />
        <div className="relative flex items-center gap-3">
          <div className="flex items-center justify-center w-11 h-11 rounded-2xl bg-indigo-400 text-white shadow-lg">
            <Building2 size={22} />
          </div>
          <span className="text-lg font-extrabold tracking-tight">Consumer Portal</span>
        </div>
        <div className="relative max-w-md">
          <h2 className="text-3xl font-extrabold leading-snug">
            Your games.<br />
            <span className="text-indigo-300">Your players.</span>
          </h2>
          <p className="mt-4 text-sm text-faint leading-relaxed">
            View assigned games, players, battles, and transactions scoped to your consumer account.
            Data is read-only — changes are managed by the platform admin.
          </p>
        </div>
        <div className="relative text-xs text-muted">
          Platform staff?{' '}
          <Link to="/login" className="text-indigo-300 hover:text-white underline">
            Admin login
          </Link>
        </div>
      </div>

      <div className="flex items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8 lg:hidden">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-indigo-600 text-white mb-4 shadow-sm">
              <Building2 size={28} />
            </div>
            <h1 className="text-2xl font-extrabold text-heading">Consumer Portal</h1>
          </div>

          <div className="bg-card rounded-2xl shadow-card-lg border border-line p-8">
            <div className="mb-6">
              <h1 className="text-2xl font-extrabold text-heading">Sign in</h1>
              <p className="text-sm text-muted mt-1">Use the portal credentials issued for your consumer.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="bg-brand-danger/10 text-brand-danger border border-brand-danger/20 px-4 py-3 rounded-xl text-sm font-semibold flex items-center gap-2">
                  <AlertCircle size={16} /> {error}
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Username</label>
                <div className="relative">
                  <User className="absolute left-3.5 top-3.5 text-faint" size={18} />
                  <input
                    type="text"
                    required
                    autoFocus
                    autoComplete="username"
                    placeholder="tp001"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full rounded-xl border border-line pl-11 pr-4 py-3 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30 sm:text-sm outline-none transition font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Password</label>
                <div className="relative">
                  <KeyRound className="absolute left-3.5 top-3.5 text-faint" size={18} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-xl border border-line pl-11 pr-11 py-3 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30 sm:text-sm outline-none transition"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-faint hover:text-heading rounded transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-xl transition-all shadow-sm disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Signing in...
                  </>
                ) : (
                  <>
                    <LogIn size={18} /> Sign In
                  </>
                )}
              </button>
            </form>
          </div>

          <p className="text-center text-xs text-faint mt-6 lg:hidden">
            <Link to="/login" className="text-indigo-600 hover:underline">
              Admin login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ConsumerLogin;
